import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getWCTLoanDetails,
  getWCTLRepaymentSchedule,
  getWorkingCapitalTermLoanWriteOffCollectionsByLoanId,
  writeOffWctlLoanByLoanId,
} from "../../../actions/loan.action";
import { showNotifier } from "../../../actions/ui.action";
import { ConfirmDialogType } from "../../../components/confirm-dialog";
import { TableColumns } from "../../../components/table";
import { WCTLLoanRepaymentSchedule, WCTLoanDetail } from "../../../dto";
import useScreenName from "../../../hooks/useScreenName";
import { ReactComponent as Loader } from "../../../images/loader.svg";
import dispatch from "../../../middleware";
import { NotifierBgColor } from "../../../models";
import { ReduxState } from "../../../reducers";
import {
  dateToDDMMYYYY,
  downloadCSV,
  isLoadingActive,
  splitKeyWithSpace,
} from "../../../utils";
import { assertWriteOffFieldsValid } from "../../personal-loan-details/helper";
import WrittenOffDetails from "../../personal-loan-details/loan-details/writtenOffDetails";
import LoanTags from "../../personal-loan-details/loanTags";
import {
  WriteOffCollectionAmount,
  WriteOffLoanFields,
} from "../../personal-loan-details/types";
import WCTLoanSummary from "./wctLoanSummary";
import { WCTLRepaymentSchedule } from "./wctlRepaymentSchedule";

const COLUMN_FOR_WCTL_REPAYMENT_SCHEDULE: TableColumns[] = [
  { field: "emiNumber", label: "EMI No" },
  { field: "dueAt", label: "Date" },
  { field: "principal", label: "Principal" },
  { field: "interest", label: "Interest" },
  { field: "totalAmount", label: "EMI" },
  { field: "status", label: "Status" },
  { field: "repaidAmount", label: "Repaid Amount" },
];

const getTableDataForRepaymentSchedule = (
  wctlLoanRepaymentSchedule: WCTLLoanRepaymentSchedule[]
) => {
  return wctlLoanRepaymentSchedule.map(
    (
      { dueAt, repaymentPendingAmount, interest, loanId, principal, status },
      idx
    ) => ({
      emiNumber: idx + 1,
      dueAt: dateToDDMMYYYY(dueAt),
      principal,
      interest,
      totalAmount: (principal + interest).toFixed(2),
      status: splitKeyWithSpace(status),
      repaidAmount: principal + interest - repaymentPendingAmount,
    })
  );
};

export default function WCTLoanDetailsScreen() {
  useScreenName("Loans");
  const { loanId }: { loanId: string } = useParams();
  const storeDispatch = useDispatch();
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue
  );
  const wctLoanDetails: WCTLoanDetail | null = useSelector(
    (state: ReduxState) => state.loan.wctlLoan
  );
  const wctlLoanRepaymentSchedule: WCTLLoanRepaymentSchedule[] = useSelector(
    (state: ReduxState) => state.loan.wctlLoanRepaymentSchedule
  );
  const isLoading = isLoadingActive(loadingQueue);
  const [errorMessage, setErrorMessage] = useState("");
  const [writeOffCollectionAmount, setWriteOffCollectionAmount] =
    useState<WriteOffCollectionAmount | null>(null);

  const fetchWCTLoanDetails = useCallback(async () => {
    try {
      await dispatch(storeDispatch, getWCTLoanDetails(loanId));
    } catch (error) {
      setErrorMessage(error?.message);
    }
  }, [storeDispatch, loanId]);

  const fetchWCTLRepaymentScheduleDetails = useCallback(async () => {
    try {
      await dispatch(storeDispatch, getWCTLRepaymentSchedule(loanId));
    } catch (error) {
      setErrorMessage(error?.message);
    }
  }, [storeDispatch, loanId]);

  function handleDownloadRepaymentSchedule() {
    downloadCSV(
      getTableDataForRepaymentSchedule(wctlLoanRepaymentSchedule),
      COLUMN_FOR_WCTL_REPAYMENT_SCHEDULE,
      `${loanId}_Repayment_Schedule`
    );
  }

  async function writeOffWctlLoan(loanId: string, body: WriteOffLoanFields) {
    try {
      const response: WCTLoanDetail | null = await dispatch(
        storeDispatch,
        writeOffWctlLoanByLoanId(loanId, body)
      );

      if (response) {
        fetchWCTLoanDetails();
      }
    } catch (error) {
      showNotifier(
        NotifierBgColor.ERROR,
        error?.message || "An unknown error occurred"
      );
    }
  }

  async function handleWriteOffBtnClick(
    type: ConfirmDialogType,
    fieldData: WriteOffLoanFields
  ) {
    try {
      const { writeOffDate, reason } = fieldData;
      assertWriteOffFieldsValid(fieldData);
      const formattedDate = new Date(writeOffDate).toISOString();
      await writeOffWctlLoan(loanId, {
        writeOffDate: formattedDate,
        reason,
      });
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Something went wrong";
      showNotifier(errorMsg, "error");
    }
  }

  const fetchWriteOffCollectionAmount = useCallback(
    async (loanId: string) => {
      try {
        const response: WriteOffCollectionAmount = await dispatch(
          storeDispatch,
          getWorkingCapitalTermLoanWriteOffCollectionsByLoanId(loanId)
        );

        if (!response) return;

        setWriteOffCollectionAmount(response);
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error?.message || "An unknown error occurred"
        );
      }
    },
    [storeDispatch]
  );

  useEffect(() => {
    fetchWCTLoanDetails();
    fetchWCTLRepaymentScheduleDetails();
    fetchWriteOffCollectionAmount(loanId);
  }, [
    fetchWCTLRepaymentScheduleDetails,
    fetchWCTLoanDetails,
    fetchWriteOffCollectionAmount,
    loanId,
  ]);

  if (!wctLoanDetails) return null;

  if (isLoading || !wctLoanDetails)
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Loader />
      </div>
    );

  if (errorMessage) {
    return <h4 className="text-center">{errorMessage}</h4>;
  }

  return (
    <>
      <LoanTags
        loanDetails={wctLoanDetails}
        loanType="WORKING_CAPITAL_TERM_LOAN"
      />
      <div className="dashboard-container">
        <WCTLoanSummary
          loanId={loanId}
          loanDetails={wctLoanDetails}
          onWriteOffBtnClick={handleWriteOffBtnClick}
        />
      </div>
      {wctLoanDetails?.writeOffMetadata && (
        <div className="my-4 dashboard-container">
          <WrittenOffDetails
            data={wctLoanDetails.writeOffMetadata}
            writeOffCollectionAmount={writeOffCollectionAmount}
          />
        </div>
      )}
      <div className="mt-4 dashboard-container">
        <WCTLRepaymentSchedule
          loanId={loanId}
          onDownloadClick={handleDownloadRepaymentSchedule}
          isLoading={isLoading}
          columns={COLUMN_FOR_WCTL_REPAYMENT_SCHEDULE}
          data={getTableDataForRepaymentSchedule(wctlLoanRepaymentSchedule)}
        />
      </div>
    </>
  );
}
